import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";
import { authAPI } from "@/services/api";

interface AuthState {
  token: string | null;
  status: "idle" | "loading" | "success" | "error";
  error: string | null;
  homePage: string;
}

const initialState: AuthState = {
  token: localStorage.getItem("user-token"),
  status: "idle",
  error: null,
  homePage: "/activities",
};

export const loginUser = createAsyncThunk(
  "auth/loginUser",
  async (credentials: { username: string; password: string }, { rejectWithValue }) => {
    try {
      const response = await authAPI.login(credentials);
      const { token, home_page } = response.data;

      if (!token) {
        console.error("No token received from login");
        return rejectWithValue("No token received from server");
      }

      localStorage.setItem("user-token", token);

      return { token, homePage: home_page || "/activities" };
    } catch (error: unknown) {
      const err = error as { response?: { data?: { errorMessage?: string } } };
      console.error("Login error:", err);
      return rejectWithValue(err.response?.data?.errorMessage || "Login failed");
    }
  }
);

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    setToken: (state, action: PayloadAction<string>) => {
      state.token = action.payload;
      localStorage.setItem("user-token", action.payload);
    },
    logout: (state) => {
      state.token = null;
      state.status = "idle";
      state.error = null;
      localStorage.removeItem("user-token");
    },
    setHomePage: (state, action: PayloadAction<string>) => {
      state.homePage = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loginUser.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.status = "success";
        state.token = action.payload.token;
        state.homePage = action.payload.homePage;
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.status = "error";
        state.error = action.payload as string;
      });
  },
});

export const { setToken, logout, setHomePage } = authSlice.actions;
export default authSlice.reducer;
